import { Auth, Hub } from "aws-amplify";
import { message } from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAccountDetails, getAccountByEmail } from "Sdk/Actions";
import { login } from "Store/reducers/Auth/AuthSlice";
import { LoginForm } from "@/components/login-from";
import { Loader } from "@/components/Loader";
import Logo from "@/Assets/Logo/logo.png";
import AuthImage from "@/Assets/auth-image.png";
import { getLoginErrorMessage } from "Utility/authErrors";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const stateLoginAttempted = useRef(false);
  const isGoogleFlowRef = useRef(false);
  const search = location.search || "";
  const hash = window.location.hash || "";
  const isGoogleAuthCallback =
    search.includes("code=") ||
    search.includes("error=") ||
    hash.includes("id_token") ||
    hash.includes("access_token") ||
    hash.includes("error=");


  const handleSubmit = useCallback(
    async (data) => {
      setIsSubmitLoading(true);
      setLoginError(false);
      isGoogleFlowRef.current = false;
      try {
        const res = await Auth.signIn(data?.email, data?.password);
        const username = res?.attributes?.preferred_username || "";

        if (!username) {
          navigate("/onboarding", { replace: true });
          return;
        }

        const account = await getAccountDetails(username);
        if (!account) {
          navigate("/onboarding", { replace: true });
          return;
        }

        dispatch(
          login({
            token: res?.signInUserSession?.accessToken?.jwtToken,
          }),
        );

        navigate("/dashboard");
      } catch (err) {
        setLoginError(true);
        if (err?.message === "User is not confirmed.") {
          message.info("Seems like your account is not verified yet.");
          navigate("/confirm-signup", { state: { email: data?.email } });
        } else {
          const isUserNotFound =
            err?.code === "UserNotFoundException" ||
            String(err?.message || "").toLowerCase().includes("user does not exist");

          if (isUserNotFound) {
            const account = await getAccountByEmail(data?.email);
            if (account?.username) {
              message.error("An account with this email already exists. Try signing in with Google.");
              return;
            }
          }

          message.error(getLoginErrorMessage(err));
        }
      } finally {
        setIsSubmitLoading(false);
      }
    },
    [dispatch, navigate],
  );

  const handleGoogleLogin = useCallback(async () => {
    isGoogleFlowRef.current = true;
    setIsGoogleLoading(true);
    try {
      await Auth.federatedSignIn({ provider: "Google" });
    } catch (err) {
      setIsGoogleLoading(false);
      console.error("Google sign-in failed:", err);
      message.error(err?.message || "Could not start Google sign-in.");
    }
  }, []);

  const finishGoogleLogin = useCallback(async () => {
    const authUser = await Auth.currentAuthenticatedUser();
    const username = authUser?.attributes?.preferred_username || "";

    if (!username) {
      navigate("/onboarding", { replace: true });
      return;
    }

    const account = await getAccountDetails(username);
    if (!account) {
      navigate("/onboarding", { replace: true });
      return;
    }

    dispatch(
      login({
        token: authUser?.signInUserSession?.accessToken?.jwtToken,
      }),
    );

    navigate("/dashboard", { replace: true });
  }, [dispatch, navigate]);

  const stateEmail = location?.state?.email;
  const statePassword = location?.state?.password;
  const stateMessage = location?.state?.message;

  useEffect(() => {
    const msg = stateMessage || sessionStorage.getItem("loginMessage");
    if (msg) {
      message.info(msg);
      sessionStorage.removeItem("loginMessage");
    }
  }, [stateMessage]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signIn":
        case "cognitoHostedUI":
          try {
            setIsGoogleLoading(true);
            await finishGoogleLogin();
          } catch (err) {
            console.error("Error handling signed-in user:", err);
            message.error(err?.message || "Google sign-in completed, but login setup failed.");
          } finally {
            setIsGoogleLoading(false);
            isGoogleFlowRef.current = false;
          }
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          if (!isGoogleFlowRef.current && !isGoogleAuthCallback) {
            break;
          }
          console.error("Google sign-in failed:", payload.data);
          message.error("Google sign-in failed. Please try again.");
          setIsGoogleLoading(false);
          isGoogleFlowRef.current = false;
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, [finishGoogleLogin, isGoogleAuthCallback]);

  useEffect(() => {
    if (!isGoogleAuthCallback) return;

    setIsGoogleLoading(true);

    let isCancelled = false;

    const resolveGoogleLogin = async (retries = 5) => {
      try {
        await finishGoogleLogin();
      } catch (err) {
        if (isCancelled) return;

        if (retries > 0) {
          window.setTimeout(() => {
            resolveGoogleLogin(retries - 1);
          }, 600);
          return;
        }

        console.error("Google callback handling failed:", err);
        message.error(err?.message || "Google sign-in completed, but login setup failed.");
        setIsGoogleLoading(false);
        navigate("/login", { replace: true });
      }
    };

    resolveGoogleLogin();

    return () => {
      isCancelled = true;
    };
  }, [finishGoogleLogin, isGoogleAuthCallback, navigate]);

  useEffect(() => {
    if (stateLoginAttempted.current) return;
    if (stateEmail && statePassword) {
      stateLoginAttempted.current = true;
      handleSubmit({ email: stateEmail, password: statePassword });
    }
  }, [handleSubmit, stateEmail, statePassword]);

  if ((location?.state?.directLogin && !loginError) || isGoogleLoading) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-2 p-6">
        <Loader />
        <p className="text-muted-foreground text-center text-sm">
          {isGoogleLoading
            ? "Hold tight while we verify your Google sign-in and redirect you."
            : "Hold tight while we set up your account."}
        </p>
      </div>
    );
  }


  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold"
            aria-label="Inprofile home"
          >
            <img src={Logo} alt="Inprofile" className="h-8 w-8" />
            Inprofile
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <LoginForm
              onSubmit={handleSubmit}
              onGoogleLogin={handleGoogleLogin}
              isSubmitLoading={isSubmitLoading}
              isGoogleLoading={isGoogleLoading}
              initialEmail={stateEmail}
              initialPassword={statePassword}
            />
          </div>
        </div>
      </div>
      <div className="bg-[#f2f5f8] relative hidden lg:block">
        <img
          src={AuthImage}
          alt=""
          className="absolute inset-0 h-full w-full object-contain dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}
